const DAY_MS = 24 * 60 * 60 * 1000;

export const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const dayDiff = (a, b) => Math.round((a.getTime() - b.getTime()) / DAY_MS);

export function eventDayRange(event) {
  const start = new Date(event.occurrenceStart);
  const first = startOfDay(start);
  if (!event.occurrenceEnd) return { first, last: first };
  const end = new Date(event.occurrenceEnd);
  if (!(end > start)) return { first, last: first };
  const lastMoment = new Date(end);
  if (
    lastMoment.getHours() === 0 &&
    lastMoment.getMinutes() === 0 &&
    lastMoment.getSeconds() === 0
  ) {
    lastMoment.setMilliseconds(-1);
  }
  const last = startOfDay(lastMoment);
  return { first, last: last < first ? first : last };
}

export function buildWeeks(monthDate) {
  const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  const weeks = [];
  for (let i = 0; i < 6; i++) {
    const week = [];
    for (let j = 0; j < 7; j++) {
      week.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

export function layoutWeek(weekDays, events, maxLanes = 3) {
  const weekStart = startOfDay(weekDays[0]);
  const weekEnd = startOfDay(weekDays[6]);

  const items = [];
  events.forEach((event) => {
    const { first, last } = eventDayRange(event);
    if (last < weekStart || first > weekEnd) return;
    items.push({
      event,
      first,
      last,
      startCol: Math.max(dayDiff(first, weekStart), 0),
      endCol: Math.min(dayDiff(last, weekStart), 6),
      continuesBefore: first < weekStart,
      continuesAfter: last > weekEnd,
      startTime: new Date(event.occurrenceStart).getTime(),
    });
  });

  items.sort(
    (a, b) =>
      a.startCol - b.startCol ||
      b.endCol - a.endCol ||
      a.startTime - b.startTime
  );

  const laneEnds = [];
  const hidden = [0, 0, 0, 0, 0, 0, 0];
  const segments = [];
  items.forEach((item) => {
    let lane = laneEnds.findIndex((end) => end < item.startCol);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = item.endCol;
    if (lane < maxLanes) {
      segments.push({ ...item, lane });
    } else {
      for (let c = item.startCol; c <= item.endCol; c++) hidden[c]++;
    }
  });

  const more = [];
  hidden.forEach((count, col) => {
    if (count > 0) more.push({ col, count });
  });

  return {
    segments,
    more,
    laneCount: Math.min(laneEnds.length, maxLanes) + (more.length ? 1 : 0),
  };
}

export function eventsOnDay(events, day) {
  const d = startOfDay(day);
  return events
    .filter((event) => {
      const { first, last } = eventDayRange(event);
      return first <= d && d <= last;
    })
    .sort(
      (a, b) =>
        new Date(a.occurrenceStart).getTime() -
        new Date(b.occurrenceStart).getTime()
    );
}

const timeFmt = new Intl.DateTimeFormat("de-DE", {
  hour: "2-digit",
  minute: "2-digit",
});
const dayFmt = new Intl.DateTimeFormat("de-DE", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
});
const dayYearFmt = new Intl.DateTimeFormat("de-DE", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

export function formatEventRange(event) {
  const start = new Date(event.occurrenceStart);
  const { first, last } = eventDayRange(event);
  const end = event.occurrenceEnd ? new Date(event.occurrenceEnd) : null;
  if (isSameDay(first, last)) {
    const time =
      end && end > start
        ? `${timeFmt.format(start)}–${timeFmt.format(end)}`
        : timeFmt.format(start);
    return `${dayYearFmt.format(start)} · ${time} Uhr`;
  }
  return `${dayFmt.format(start)} ${timeFmt.format(start)} – ${dayFmt.format(
    end
  )} ${timeFmt.format(end)} Uhr`;
}

export const formatTime = (date) => timeFmt.format(new Date(date));
